import BaseNotificationHandler from './notificationHandlers/baseHandler.js';
import ReactionHandler from './notificationHandlers/reactionHandler.js';
import ConnectionRequestHandler from './notificationHandlers/connectionRequestHandler.js';
import AppointmentHandler from './notificationHandlers/appointmentHandler.js';
import VisitHandler from './notificationHandlers/visitHandler.js';
import ClinicInvitationHandler from './notificationHandlers/clinicInvitationHandler.js';
import CommentHandler from './notificationHandlers/commentHandler.js';
import ChannelNotificationHandler from './notificationHandlers/channelHandler.js';
import GuardianshipHandler from './notificationHandlers/guardianshipHandler.js';
import DefaultHandler from './notificationHandlers/defaultHandler.js';
import RepostHandler from './notificationHandlers/repostHandler.js';
import PostDeletedByAdminHandler from './notificationHandlers/postDeletedByAdminHandler.js';

/**
 * Notification Service
 * Centralized service for handling all notification types using a registry pattern
 */
class NotificationService {
  constructor() {
    this.handlers = new Map();
    this.defaultHandler = new DefaultHandler();
    this.initializeDefaultHandlers();
  }

  /**
   * Initialize default notification type handlers
   */
  initializeDefaultHandlers() {
    // Register reaction handler
    const reactionHandler = new ReactionHandler();
    this.registerNotificationType('reaction', reactionHandler);
    this.registerNotificationType('post_reaction', reactionHandler);
    this.registerNotificationType('story_reaction', reactionHandler);

    // Register connection request handler
    this.registerNotificationType('connection_request', new ConnectionRequestHandler());
    this.registerNotificationType('new_follow_request', new ConnectionRequestHandler());

    // Register appointment handlers
    this.registerNotificationType('appointment_created', new AppointmentHandler());
    this.registerNotificationType('appointment_updated', new AppointmentHandler());
    this.registerNotificationType('appointment_canceled', new AppointmentHandler());
    this.registerNotificationType('appointment_restored', new AppointmentHandler());
    this.registerNotificationType('appointment_status_changed', new AppointmentHandler());

    // Register visit handlers
    this.registerNotificationType('outpatient_visit_created', new VisitHandler());
    this.registerNotificationType('outpatient_visit_updated', new VisitHandler());
    this.registerNotificationType('outpatient_visit_status_changed', new VisitHandler());
    this.registerNotificationType('inpatient_visit_created', new VisitHandler());
    this.registerNotificationType('inpatient_visit_updated', new VisitHandler());
    this.registerNotificationType('inpatient_visit_status_changed', new VisitHandler());

    // Register clinic invitation handler
    this.registerNotificationType('clinic_invitation', new ClinicInvitationHandler());

    // Register comment handler
    this.registerNotificationType('comment', new CommentHandler());
    this.registerNotificationType('comment_reply', new CommentHandler());

    // Register repost handler
    this.registerNotificationType('repost', new RepostHandler());

    // Register channel handler for all channel notification types
    const channelHandler = new ChannelNotificationHandler();
    [
      'channel_invitation_sent',
      'channel_invitation_accepted',
      'channel_join_request_submitted',
      'channel_join_request_approved',
      'channel_join_request_rejected',
      'channel_member_removed',
      'channel_member_role_updated',
      'channel_post_pinned',
      'channel_updated',
      'channel_ownership_transferred',
    ].forEach((type) => this.registerNotificationType(type, channelHandler));

    // Register guardianship handlers
    const guardianshipHandler = new GuardianshipHandler();
    this.registerNotificationType('guardianship_invitation', guardianshipHandler);
    this.registerNotificationType('guardianship_response', guardianshipHandler);

    // Register post deleted by admin handler
    this.registerNotificationType('post_deleted_by_admin', new PostDeletedByAdminHandler());
  }

  /**
   * Register a new notification type handler
   * @param {string} type - Notification type
   * @param {BaseNotificationHandler} handler - Handler instance
   */
  registerNotificationType(type, handler) {
    if (!(handler instanceof BaseNotificationHandler)) {
      throw new Error('Handler must extend BaseNotificationHandler');
    }
    this.handlers.set(type, handler);
  }

  /**
   * Get handler for a notification type
   * @param {string} type - Notification type
   * @returns {BaseNotificationHandler} Handler instance
   */
  getNotificationHandler(type) {
    const handler = this.handlers.get(type) || this.defaultHandler;
    if (!this.handlers.has(type) && type !== 'default') {
      console.warn(`No handler registered for notification type: "${type}". Using default handler.`);
    }
    return handler;
  }

  /**
   * Parse a single notification and return processed object
   * @param {Object} notification - Raw notification object from API
   * @returns {Object} Processed notification object with all helper methods
   */
  parseNotification(notification) {
    if (!notification) {
      console.warn('Invalid notification object: notification is null or undefined');
      return null;
    }

    // If notification doesn't have a type, try to infer it or use default
    const notificationType = notification.type || 'default';

    if (!notification.type) {
      console.warn('Notification missing type field, using default handler:', notification);
    }

    const handler = this.getNotificationHandler(notificationType);
    const parsedData = handler.parseData(notification);

    // Debug logging for comment_reply
    if (notificationType === 'comment_reply') {

    }

    // Return processed notification object with all helper methods
    return {
      // Original notification data
      ...notification,

      // Parsed data
      parsedData,

      // Helper methods that return processed values
      getIcon: () => handler.getIcon(notification, parsedData),
      getMessage: () => handler.getMessage(notification, parsedData),
      getTitle: () => handler.getTitle(notification, parsedData),
      getActionUrl: () => handler.getActionUrl(notification, parsedData),
      getDetails: () => handler.getDetails(notification, parsedData),
      getThumbnail: () => handler.getThumbnail(notification, parsedData),
      getSenderInfo: () => handler.getSenderInfo(notification, parsedData),
      getSenderImage: () => handler.getSenderImage(notification, parsedData),
      getMainText: () => handler.getMainText(notification, parsedData),
      handleClick: (navigate, onClose, openPostModal) => handler.handleClick(notification, parsedData, navigate, onClose, openPostModal),
      hasActionButtons: () => handler.hasActionButtons(notification, parsedData),

      // Convenience properties (computed once for performance)
      icon: handler.getIcon(notification, parsedData),
      message: handler.getMessage(notification, parsedData),
      title: handler.getTitle(notification, parsedData),
      actionUrl: handler.getActionUrl(notification, parsedData),
      details: handler.getDetails(notification, parsedData),
      thumbnail: handler.getThumbnail(notification, parsedData),
      senderInfo: handler.getSenderInfo(notification, parsedData),
      senderImage: handler.getSenderImage(notification, parsedData),
      mainText: handler.getMainText(notification, parsedData),
      hasActionButtons: handler.hasActionButtons(notification, parsedData),
    };
  }

  /**
   * Parse an array of notifications
   * @param {Array<Object>} notifications - Array of raw notification objects
   * @returns {Array<Object>} Array of processed notification objects
   */
  parseNotifications(notifications) {
    if (!Array.isArray(notifications)) {
      console.warn('parseNotifications expects an array, got:', typeof notifications);
      return [];
    }

    return notifications
      .map(notification => this.parseNotification(notification))
      .filter(notification => notification !== null);
  }
}

// Create and export a singleton instance
const notificationService = new NotificationService();

// Export the service instance and class for testing
export default notificationService;
export { NotificationService };

